import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";

/** Rename the selected picture's file in the media library. Articles point at
 *  the asset id, so the name is only a label — but it's the only thing anyone
 *  can search on later, and a pasted screenshot arrives called "image.png". */
export function KbImageNameField({ src }) {
  const assetId = (src || "").startsWith("/api/kb/assets/")
    ? src.split("/").pop().split("?")[0] : "";
  const [name, setName] = useState("");
  const [usedIn, setUsedIn] = useState([]);
  const savedRef = useRef("");

  useEffect(() => {
    if (!assetId) return;
    api.get(`/kb/media/${assetId}`).then((r) => {
      setName(r.data.filename || "");
      savedRef.current = r.data.filename || "";
      setUsedIn(r.data.used_in || []);
    }).catch(() => { setName(""); setUsedIn([]); });
  }, [assetId]);

  const save = async () => {
    const v = name.trim();
    if (!assetId || !v || v === savedRef.current) return;
    try {
      const { data } = await api.patch(`/kb/media/${assetId}`, { filename: v });
      setName(data.filename);
      savedRef.current = data.filename;
      const others = (data.used_in || []).filter((a) => a.slug);
      toast.success(others.length > 1
        ? `Renamed — this picture is used in ${others.length} articles, they all show the new name`
        : "Renamed in the media library");
    } catch (e) {
      setName(savedRef.current);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't rename that");
    }
  };

  if (!assetId) return null;

  return (
    <span className="flex items-center gap-1.5">
      <Input data-testid="kb-rte-image-filename" value={name}
        placeholder="File name (for the media library)"
        onChange={(e) => setName(e.target.value)} onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
        className="h-7 max-w-[220px] text-xs" />
      {usedIn.length > 1 && (
        <span data-testid="kb-rte-image-usage"
          className="whitespace-nowrap rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-950 dark:text-amber-300"
          title={usedIn.map((a) => a.name).join(", ")}>
          in {usedIn.length} articles
        </span>
      )}
    </span>
  );
}
